import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { BellRing, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NotifyList = () => {
    const [email, setEmail] = useState("");
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await axios.post(`${API}/subscribe`, { email });
            setDone(true);
        } catch {
            toast.error("Could not add you — try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="relative overflow-hidden border-t border-red-950/50 py-24 lg:py-32" data-testid="notify-list-section">
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.12]"
                style={{
                    backgroundImage:
                        "url(https://images.unsplash.com/photo-1621146027714-e8921770f8d0?crop=entropy&cs=srgb&fm=jpg&q=85)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            />
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-red-900/20 blur-[120px]" />
            <div className="relative mx-auto max-w-3xl px-6 text-center sm:px-12">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="font-mono2 text-xs uppercase tracking-[0.35em] text-red-500"
                >
                    Grand Opening
                </motion.p>
                <motion.h2
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="mt-4 font-display text-2xl font-extrabold tracking-tight text-bone sm:text-3xl lg:text-4xl"
                >
                    Be first <span className="font-editorial italic text-red-500">through the door.</span>
                </motion.h2>
                <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, delay: 0.25 }}
                    className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-ash"
                >
                    Sales open soon. Leave your email and you'll be the first to know when the
                    collection goes live — rare one-of-one animals move fast.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, delay: 0.35 }}
                    className="mt-10"
                >
                    {done ? (
                        <p
                            data-testid="notify-success"
                            className="mx-auto flex max-w-md items-center justify-center gap-3 border border-emerald-800/60 bg-emerald-950/30 px-6 py-4 font-mono2 text-xs uppercase tracking-[0.2em] text-emerald-400"
                        >
                            <CheckCircle2 className="h-4 w-4" /> You're on the list — watch your inbox
                        </p>
                    ) : (
                        <form onSubmit={submit} data-testid="notify-form" className="mx-auto flex max-w-md gap-3">
                            <input
                                data-testid="notify-email-input"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@email.com"
                                className="flex-1 border border-red-950/60 bg-black/60 px-5 py-4 text-sm text-bone placeholder:text-ash/40 outline-none backdrop-blur-sm transition-colors duration-300 focus:border-red-700"
                            />
                            <button
                                data-testid="notify-submit-button"
                                type="submit"
                                disabled={busy}
                                className="flex items-center gap-2 bg-crimson px-6 py-4 font-mono2 text-[11px] uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-red-700 disabled:opacity-50"
                            >
                                <BellRing className="h-4 w-4" /> {busy ? "Joining…" : "Notify Me"}
                            </button>
                        </form>
                    )}
                </motion.div>
            </div>
        </section>
    );
};
